import React from "react";

import {
  Card,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from "@mui/material";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { useFormatNumericHook, useNavigate } from "hooks";
import type { IClientOverviewDeal, IClientOverviewDealsPostfix } from "store/types/client/client-overview";

interface Props {
  deals: IClientOverviewDeal[];
  count: number;
  postfix?: IClientOverviewDealsPostfix;
  loading?: boolean;
}

const HEADERS = ["Deal", "Asset Class", "Commitment", "NAV", "MOIC", "IRR"] as const;

const CELL_SX = {
  whiteSpace: "nowrap",
  textOverflow: "ellipsis",
  overflow: "hidden",
  padding: "0 20px",
  color: "#0a3142",
  fontSize: "16px",
  height: "50px"
} as const;

/** Linked deals preview table on the client overview. */
export const LinkedDeals: React.FC<Props> = ({ deals, count, postfix, loading = false }) => {
  const navigate = useNavigate();
  const formatNumeric = useFormatNumericHook();

  // Format a metric value using the backend-provided unit: `$` prefixes, `%` is a
  // suffix (value is a ratio → ×100), anything else is a trailing unit.
  const formatValue = (value: number | null, unit?: string): string => {
    if (value == null) return "—";
    const u = (unit ?? "").trim();
    if (u === "%") return `${formatNumeric({ value: value * 100, format: {} })}%`;
    if (u === "$") return `$${formatNumeric({ value, format: {} })}`;
    return u ? `${formatNumeric({ value, format: {} })} ${u}` : `${formatNumeric({ value, format: {} })}`;
  };

  return (
    <Card sx={{ bgcolor: "background.paper", boxShadow: "sm", borderRadius: 8, p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="body1" sx={{ fontWeight: 500 }}>
          Linked deals ({count})
        </Typography>
      </Stack>
      {loading ? (
        <Stack sx={{ gap: 1 }}>
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} variant="rounded" height={44} />
          ))}
        </Stack>
      ) : deals.length > 0 ? (
        // Height fits the header + 5 rows (50px each); the rest scrolls under a sticky header.
        <TableContainer sx={{ borderRadius: "10px", border: "1px solid #dae0e2", maxHeight: 300, overflowY: "auto" }}>
          <Table stickyHeader sx={{ color: "#0a3142" }}>
            <TableHead>
              <TableRow>
                {HEADERS.map((header) => (
                  <TableCell
                    key={header}
                    align={header === "Deal" || header === "Asset Class" ? "left" : "center"}
                    sx={{ ...CELL_SX, fontSize: "15px", fontWeight: "normal", backgroundColor: "#edf1f9" }}
                  >
                    {header}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {deals.map((deal) => (
                <TableRow
                  key={deal.deal_id}
                  onClick={() => navigate({ route: "deal", params: { deal_id: deal.deal_id } })}
                  sx={{ cursor: "pointer", ":hover": { backgroundColor: "#f5f7f7" } }}
                >
                  <TableCell sx={{ ...CELL_SX, maxWidth: 220 }}>
                    <MaterialTooltip title={deal.deal_name} variant="dark">
                      <Typography sx={{ fontSize: "16px", color: "#26A6AE" }} noWrap>
                        {deal.deal_name}
                      </Typography>
                    </MaterialTooltip>
                  </TableCell>
                  <TableCell sx={CELL_SX}>{deal.asset_class?.name ?? "—"}</TableCell>
                  <TableCell align="center" sx={CELL_SX}>
                    {formatValue(deal.commitment, postfix?.commitment)}
                  </TableCell>
                  <TableCell align="center" sx={CELL_SX}>
                    {formatValue(deal.nav, postfix?.nav)}
                  </TableCell>
                  <TableCell align="center" sx={CELL_SX}>
                    {formatValue(deal.moic, postfix?.moic)}
                  </TableCell>
                  <TableCell align="center" sx={CELL_SX}>
                    {formatValue(deal.irr, postfix?.irr)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          No linked deals yet.
        </Typography>
      )}
    </Card>
  );
};

export default LinkedDeals;
